import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type CreateFeatureFlagPayload,
  type FeatureFlag,
  type FeatureFlagMiss,
  featureFlagsProvider,
  type MissUniverse,
  type UpdateFeatureFlagPayload,
  type UserFeatureOverride,
} from "@/providers/feature-flags.provider";
import { type User, usersProvider } from "@/providers/users.provider";
import {
  IconAlertTriangle,
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconFlag,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconUsers,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// ── Types ─────────────────────────────────────────────────────────────────────

type SheetMode =
  | { type: "closed" }
  | { type: "create"; initialKey?: string }
  | { type: "edit"; flag: FeatureFlag }
  | { type: "users"; flagKey: string };

// ── Shared ────────────────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        checked ? "bg-primary" : "bg-input"
      }`}
    >
      <span
        className={`pointer-events-none inline-block size-4 rounded-full bg-background shadow-lg ring-0 transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// ── Flag table row ────────────────────────────────────────────────────────────

function RolloutBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-xs tabular-nums">{value}%</span>
    </div>
  );
}

function FlagRow({
  flag,
  onEdit,
  onDelete,
  isDeletePending,
  onViewUsers,
}: {
  flag: FeatureFlag;
  onEdit: (flag: FeatureFlag) => void;
  onDelete: (key: string) => void;
  isDeletePending: boolean;
  onViewUsers: (key: string) => void;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono font-medium">{flag.key}</TableCell>
      <TableCell className="max-w-[260px] truncate text-muted-foreground">
        {flag.description || <span className="italic opacity-40">—</span>}
      </TableCell>
      <TableCell>
        <RolloutBar value={flag.rollout_percentage} />
      </TableCell>
      <TableCell className="text-muted-foreground text-xs">
        {new Date(flag.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            title="Manage user overrides"
            onClick={() => onViewUsers(flag.key)}
          >
            <IconUsers className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            title="Edit flag"
            onClick={() => onEdit(flag)}
          >
            <IconEdit className="size-4" />
          </Button>
          <ConfirmDestructiveDialog
            confirmValue={flag.key}
            confirmLabel="flag key"
            title={`Delete flag "${flag.key}"?`}
            description="This action cannot be undone."
            isPending={isDeletePending}
            onConfirm={() => onDelete(flag.key)}
            trigger={({ disabled }) => (
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-destructive hover:text-destructive"
                title="Delete flag"
                disabled={disabled}
              >
                <IconTrash className="size-4" />
              </Button>
            )}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Create / Edit Sheet ───────────────────────────────────────────────────────

function FlagFormSheet({
  mode,
  onClose,
}: {
  mode: Extract<SheetMode, { type: "create" | "edit" }>;
  onClose: () => void;
}) {
  const isEdit = mode.type === "edit";
  const existing = isEdit ? mode.flag : null;

  const [key, setKey] = useState(
    existing?.key ?? (mode.type === "create" ? (mode.initialKey ?? "") : ""),
  );
  const [description, setDescription] = useState(existing?.description ?? "");
  const [rollout, setRollout] = useState(existing?.rollout_percentage ?? 0);

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (payload: CreateFeatureFlagPayload) =>
      featureFlagsProvider.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
      toast.success("Flag created");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      key,
      payload,
    }: {
      key: string;
      payload: UpdateFeatureFlagPayload;
    }) => featureFlagsProvider.update(key, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
      toast.success("Flag updated");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isEdit && existing) {
      updateMutation.mutate({
        key: existing.key,
        payload: { description, rollout_percentage: rollout },
      });
    } else {
      createMutation.mutate({ key, description, rollout_percentage: rollout });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="contents">
      <SheetHeader>
        <SheetTitle>{isEdit ? "Edit Flag" : "Create Flag"}</SheetTitle>
        <SheetDescription>
          {isEdit
            ? "Update the flag's settings. The key cannot be changed."
            : "Define a new feature flag. The key is permanent and must be unique."}
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-col gap-4 px-4 py-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="flag-key">Key</Label>
          <Input
            id="flag-key"
            placeholder="e.g. new-dashboard"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={isEdit}
            required={!isEdit}
            className="font-mono"
          />
          {isEdit && (
            <p className="text-xs text-muted-foreground">
              Keys are immutable after creation.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="flag-desc">Description</Label>
          <Input
            id="flag-desc"
            placeholder="What does this flag control?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="flag-rollout">Rollout percentage — {rollout}%</Label>
          <input
            id="flag-rollout"
            type="range"
            min={0}
            max={100}
            value={rollout}
            onChange={(e) => setRollout(Number(e.target.value))}
            className="w-full accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0% — disabled for all</span>
            <span>100% — enabled for all</span>
          </div>
        </div>
      </div>

      <SheetFooter>
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Saving…" : isEdit ? "Save changes" : "Create flag"}
        </Button>
      </SheetFooter>
    </form>
  );
}

// ── User Picker ───────────────────────────────────────────────────────────────

function UserPicker({
  selected,
  onSelect,
}: {
  selected: User | null;
  onSelect: (user: User | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const { data: results, isFetching } = useQuery({
    queryKey: ["users-search", debouncedQuery],
    queryFn: () => usersProvider.search(debouncedQuery),
    enabled: debouncedQuery.length >= 1,
    staleTime: 30_000,
  });

  if (selected) {
    return (
      <div className="flex items-center justify-between rounded-lg border px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{selected.username}</p>
          <p className="truncate text-xs text-muted-foreground font-mono">
            {selected.user_id}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0 text-xs"
          onClick={() => {
            onSelect(null);
            setQuery("");
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        placeholder="Search by username, email or id…"
        value={query}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && query.trim().length >= 1 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-md">
          {isFetching ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Searching…
            </p>
          ) : !results?.length ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              No users found
            </p>
          ) : (
            <>
              <div className="max-h-48 overflow-y-auto">
                {results.map((u) => (
                  <button
                    key={u.user_id}
                    type="button"
                    className="flex w-full flex-col px-3 py-2 text-left hover:bg-accent"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onSelect(u);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span className="text-sm font-medium">{u.username}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {u.user_id}
                    </span>
                  </button>
                ))}
              </div>
              {results.length === 50 && (
                <p className="border-t px-3 py-1.5 text-xs text-muted-foreground">
                  50 results shown — refine your search to narrow down
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Users Override Sheet ──────────────────────────────────────────────────────

const PAGE_SIZE = 50;

function UsersSheet({
  flagKey,
  onClose,
}: {
  flagKey: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newEnabled, setNewEnabled] = useState(true);

  const overridesKey = ["feature-flag-overrides", flagKey, page];

  const { data: overrides, isLoading } = useQuery({
    queryKey: overridesKey,
    queryFn: () => featureFlagsProvider.listOverrides(flagKey, page),
  });

  const setOverrideMutation = useMutation({
    mutationFn: ({
      userId,
      isEnabled,
    }: {
      userId: string;
      isEnabled: boolean;
    }) => featureFlagsProvider.setUserOverride(flagKey, userId, isEnabled),
    onSuccess: (_, { userId, isEnabled }) => {
      queryClient.setQueryData<UserFeatureOverride[]>(
        overridesKey,
        (prev) =>
          prev?.map((o) =>
            o.user_id === userId ? { ...o, is_enabled: isEnabled } : o,
          ) ?? [],
      );
      toast.success("Override updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const addOverrideMutation = useMutation({
    mutationFn: ({
      userId,
      isEnabled,
    }: {
      userId: string;
      isEnabled: boolean;
    }) => featureFlagsProvider.setUserOverride(flagKey, userId, isEnabled),
    onSuccess: () => {
      setPage(1);
      queryClient.invalidateQueries({
        queryKey: ["feature-flag-overrides", flagKey],
      });
      setSelectedUser(null);
      setNewEnabled(true);
      toast.success("Override saved");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      featureFlagsProvider.removeUserOverride(flagKey, userId),
    onSuccess: (_, userId) => {
      queryClient.setQueryData<UserFeatureOverride[]>(
        overridesKey,
        (prev) => prev?.filter((o) => o.user_id !== userId) ?? [],
      );
      toast.success("Override removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleAddOverride(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUser) return;
    addOverrideMutation.mutate({
      userId: selectedUser.user_id,
      isEnabled: newEnabled,
    });
  }

  const isBusy = setOverrideMutation.isPending || removeMutation.isPending;
  const hasMore = (overrides?.length ?? 0) === PAGE_SIZE;

  return (
    <>
      <SheetHeader>
        <SheetTitle>User overrides</SheetTitle>
        <SheetDescription>
          Per-user overrides for{" "}
          <span className="font-mono font-medium text-foreground">
            {flagKey}
          </span>
          . Overrides bypass rollout percentage entirely.
        </SheetDescription>
      </SheetHeader>

      {/* Add / update override form */}
      <form
        onSubmit={handleAddOverride}
        className="mx-4 flex flex-col gap-3 rounded-lg border p-3"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Add / update override
        </p>
        <div className="flex flex-col gap-1.5">
          <Label>User</Label>
          <UserPicker selected={selectedUser} onSelect={setSelectedUser} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Enabled</p>
            <p className="text-xs text-muted-foreground">
              Force-enable or force-disable for this user.
            </p>
          </div>
          <Toggle checked={newEnabled} onChange={setNewEnabled} />
        </div>
        <Button
          type="submit"
          size="sm"
          disabled={addOverrideMutation.isPending || !selectedUser}
          className="gap-1.5"
        >
          <IconPlus className="size-3.5" />
          {addOverrideMutation.isPending ? "Saving…" : "Save override"}
        </Button>
      </form>

      {/* Overrides list */}
      <div className="flex flex-1 flex-col overflow-hidden px-4 py-2">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          All overrides — page {page}
        </p>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : !overrides?.length ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
              <IconUsers className="size-8 opacity-40" />
              <p className="text-sm">
                {page > 1
                  ? "No more overrides on this page."
                  : "No overrides set yet."}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {overrides.map((override) => (
                <div
                  key={override.user_id}
                  className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {override.username}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {override.email}
                    </p>
                  </div>
                  <Toggle
                    checked={override.is_enabled}
                    disabled={isBusy}
                    onChange={(v) =>
                      setOverrideMutation.mutate({
                        userId: override.user_id,
                        isEnabled: v,
                      })
                    }
                  />
                  <ConfirmDestructiveDialog
                    skipFeatureFlag
                    title="Remove user override?"
                    description={
                      <>
                        This removes the override for{" "}
                        <span className="font-medium text-foreground">
                          {override.username}
                        </span>{" "}
                        ({override.email}). They will fall back to the
                        default rollout behavior for this flag.
                      </>
                    }
                    actionLabel="Remove"
                    isPending={
                      removeMutation.isPending &&
                      removeMutation.variables === override.user_id
                    }
                    onConfirm={() => removeMutation.mutate(override.user_id)}
                    trigger={({ disabled }) => (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 shrink-0 text-destructive hover:text-destructive"
                        disabled={isBusy || disabled}
                        title="Remove override"
                      >
                        <IconTrash className="size-3.5" />
                      </Button>
                    )}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {(page > 1 || hasMore) && (
          <div className="mt-3 flex items-center justify-between border-t pt-3">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="gap-1"
            >
              <IconChevronLeft className="size-3.5" />
              Prev
            </Button>
            <span className="text-xs text-muted-foreground">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              className="gap-1"
            >
              Next
              <IconChevronRight className="size-3.5" />
            </Button>
          </div>
        )}
      </div>

      <SheetFooter>
        <Button variant="outline" className="w-full" onClick={onClose}>
          Close
        </Button>
      </SheetFooter>
    </>
  );
}

// ── Misses Tab ────────────────────────────────────────────────────────────────

const UNIVERSES: { value: MissUniverse; label: string }[] = [
  { value: "dev", label: "Dev" },
  { value: "staging", label: "Staging" },
  { value: "prod", label: "Prod" },
];

const UNIVERSE_STYLES: Record<MissUniverse, string> = {
  dev: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
  staging:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400",
  prod: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
};

function MissesTab({ onCreateFlag }: { onCreateFlag: (key: string) => void }) {
  const queryClient = useQueryClient();
  const [universe, setUniverse] = useState<MissUniverse | undefined>(undefined);

  const queryKey = ["feature-flag-misses", universe ?? "all"];

  const {
    data: misses,
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: () => featureFlagsProvider.listMisses(universe),
  });

  const deleteMissMutation = useMutation({
    mutationFn: (key: string) => featureFlagsProvider.deleteMiss(key),
    onSuccess: (_, key) => {
      queryClient.setQueryData<FeatureFlagMiss[]>(
        queryKey,
        (prev) => prev?.filter((m) => m.key !== key) ?? [],
      );
      toast.success("Miss cleared");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground max-w-xl">
          A miss is recorded every time a client calls the feature check
          endpoint for a key that doesn't exist. High counts usually mean a typo
          in frontend code or a stale reference to a deleted flag.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex items-center rounded-lg border p-0.5">
            <button
              type="button"
              onClick={() => setUniverse(undefined)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                universe === undefined
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              All
            </button>
            {UNIVERSES.map((u) => (
              <button
                key={u.value}
                type="button"
                onClick={() => setUniverse(u.value)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  universe === u.value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {u.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => queryClient.invalidateQueries({ queryKey })}
          >
            <IconRefresh className="size-3.5" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Universe</TableHead>
              <TableHead>First seen</TableHead>
              <TableHead className="text-right">Miss count</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load misses. Check your connection and try again.
                </TableCell>
              </TableRow>
            ) : !misses?.length ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-12 text-center text-muted-foreground"
                >
                  No misses — every key checked by clients exists in the system.
                </TableCell>
              </TableRow>
            ) : (
              misses.map((miss) => (
                <TableRow key={miss.key}>
                  <TableCell className="font-mono font-medium">
                    {miss.key}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${UNIVERSE_STYLES[miss.universe]}`}
                    >
                      {miss.universe}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(miss.first_seen_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <span
                      className={`font-semibold tabular-nums ${
                        miss.miss_count >= 20
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-foreground"
                      }`}
                    >
                      {miss.miss_count.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => onCreateFlag(miss.key)}
                      >
                        <IconPlus className="size-3" />
                        Create flag
                      </Button>
                      <ConfirmDestructiveDialog
                        title={`Clear miss "${miss.key}"?`}
                        description="This clears the recorded miss telemetry for this key. It does not affect any flag or client behavior."
                        actionLabel="Clear"
                        skipFeatureFlag
                        isPending={
                          deleteMissMutation.isPending &&
                          deleteMissMutation.variables === miss.key
                        }
                        onConfirm={() => deleteMissMutation.mutate(miss.key)}
                        trigger={({ disabled }) => (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-destructive hover:text-destructive"
                            disabled={disabled}
                            title="Clear miss"
                          >
                            <IconTrash className="size-4" />
                          </Button>
                        )}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function FeatureFlagsPage() {
  const [sheet, setSheet] = useState<SheetMode>({ type: "closed" });
  const queryClient = useQueryClient();

  const {
    data: flags,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: () => featureFlagsProvider.list(),
  });

  const { data: misses } = useQuery({
    queryKey: ["feature-flag-misses", "all"],
    queryFn: () => featureFlagsProvider.listMisses(),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => featureFlagsProvider.delete(key),
    onSuccess: (_, key) => {
      queryClient.setQueryData<FeatureFlag[]>(
        ["feature-flags"],
        (prev) => prev?.filter((f) => f.key !== key) ?? [],
      );
      toast.success("Flag deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const [activeTab, setActiveTab] = useState("flags");
  const isSheetOpen = sheet.type !== "closed";
  const missCount = misses?.length ?? 0;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Feature Flags" />

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col flex-1 overflow-hidden"
      >
        {/* Tab bar */}
        <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-2 lg:px-6 lg:pt-6">
          <TabsList>
            <TabsTrigger value="flags" className="gap-1.5">
              <IconFlag className="size-4" />
              Flags
              {flags && (
                <span className="rounded-full bg-muted-foreground/15 px-1.5 py-px text-xs tabular-nums">
                  {flags.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="misses" className="gap-1.5">
              <IconAlertTriangle className="size-4" />
              Misses
              {missCount > 0 && (
                <span className="rounded-full bg-amber-500/15 px-1.5 py-px text-xs tabular-nums text-amber-700 dark:text-amber-400">
                  {missCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {activeTab === "flags" && (
            <Button
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={() =>
                setSheet({ type: "create", initialKey: undefined })
              }
            >
              <IconPlus className="size-4" />
              New flag
            </Button>
          )}
        </div>

        {/* Flags tab */}
        <TabsContent
          value="flags"
          className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6"
        >
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Rollout</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-12 text-center text-muted-foreground"
                    >
                      Failed to load flags. Check your connection and try again.
                    </TableCell>
                  </TableRow>
                ) : !flags?.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No feature flags yet.{" "}
                      <button
                        className="underline hover:text-foreground"
                        onClick={() =>
                          setSheet({ type: "create", initialKey: undefined })
                        }
                      >
                        Create one
                      </button>
                      .
                    </TableCell>
                  </TableRow>
                ) : (
                  flags.map((flag) => (
                    <FlagRow
                      key={flag.key}
                      flag={flag}
                      onEdit={(f) => setSheet({ type: "edit", flag: f })}
                      onDelete={(key) => deleteMutation.mutate(key)}
                      isDeletePending={
                        deleteMutation.isPending &&
                        deleteMutation.variables === flag.key
                      }
                      onViewUsers={(key) =>
                        setSheet({ type: "users", flagKey: key })
                      }
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Misses tab */}
        <TabsContent
          value="misses"
          className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6"
        >
          <MissesTab
            onCreateFlag={(key) =>
              setSheet({ type: "create", initialKey: key })
            }
          />
        </TabsContent>
      </Tabs>

      {/* Create / Edit Sheet */}
      <Sheet
        open={isSheetOpen && (sheet.type === "create" || sheet.type === "edit")}
        onOpenChange={(open) => {
          if (!open) setSheet({ type: "closed" });
        }}
      >
        <SheetContent side="right" className="sm:max-w-md w-full">
          {(sheet.type === "create" || sheet.type === "edit") && (
            <FlagFormSheet
              mode={sheet}
              onClose={() => setSheet({ type: "closed" })}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Users Override Sheet */}
      <Sheet
        open={isSheetOpen && sheet.type === "users"}
        onOpenChange={(open) => {
          if (!open) setSheet({ type: "closed" });
        }}
      >
        <SheetContent side="right" className="sm:max-w-md w-full flex flex-col">
          {sheet.type === "users" && (
            <UsersSheet
              flagKey={sheet.flagKey}
              onClose={() => setSheet({ type: "closed" })}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
